import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Trophy, Lock, CheckCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import Header from '../components/Header';
import axios from 'axios';
import { BACKEND_URL } from '../config';
import './Achievements.css';

const API = `${BACKEND_URL}/api`;

const Achievements = () => {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [allAchievements, setAllAchievements] = useState([]);
  const [earnedAchievements, setEarnedAchievements] = useState([]);
  const [groupedAchievements, setGroupedAchievements] = useState({});

  useEffect(() => {
    fetchAchievements();
  }, [isAuthenticated]);

  const fetchAchievements = async () => {
    try {
      setLoading(true);
      
      // Always fetch all available achievements (public endpoint)
      const allResponse = await axios.get(`${API}/achievements/all`);
      setAllAchievements(allResponse.data.achievements);
      
      let earnedAchievementsData = [];
      
      // Only fetch user's earned achievements if authenticated
      if (isAuthenticated) {
        const token = localStorage.getItem('token');
        if (token) {
          try {
            const earnedResponse = await axios.get(`${API}/achievements/user`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            earnedAchievementsData = earnedResponse.data.achievements;
            setEarnedAchievements(earnedAchievementsData);
          } catch (error) {
            console.error('Error fetching user achievements:', error);
          }
        }
      }
      
      // Group achievements by theme
      const grouped = {};
      allResponse.data.achievements.forEach(achievement => {
        const theme = achievement.theme;
        if (!grouped[theme]) {
          grouped[theme] = [];
        }
        const isEarned = earnedAchievementsData.some(
          earned => earned.achievement_id === achievement.id
        );
        grouped[theme].push({
          ...achievement,
          earned: isEarned,
          earnedData: isEarned 
            ? earnedAchievementsData.find(e => e.achievement_id === achievement.id)
            : null
        });
      });
      setGroupedAchievements(grouped);
      
    } catch (error) {
      console.error('Error fetching achievements:', error);
    } finally {
      setLoading(false);
    }
  };

  const getProgressStats = () => {
    const total = allAchievements.length;
    const earned = earnedAchievements.length;
    const percentage = total > 0 ? Math.round((earned / total) * 100) : 0;
    return { total, earned, percentage };
  };

  const stats = getProgressStats();

  return (
    <>
      <Header />
      <div className="achievements-page">
      
        {/* Hero Section */}
        <div className="achievements-hero">
          <span className="achievements-hero-emoji">🏆</span>
          <h1 className="achievements-hero-title">My Achievements</h1>
          
          <div className="achievements-stats">
            <span className="achievements-stat">{stats.earned} Unlocked</span>
            <span className="achievements-stat">/</span>
            <span className="achievements-stat">{stats.total} Total</span>
          </div>
          
          <div className="achievements-progress-bar">
            <div 
              className="achievements-progress-fill" 
              style={{ width: `${stats.percentage}%` }}
            />
          </div>
        </div>

        <div className="achievements-container">

          {/* Achievements Grid */}
          {loading ? (
            <div className="achievements-loading">
              <div className="achievements-loading-spinner" />
              <p className="achievements-loading-text">Loading achievements...</p>
            </div>
          ) : allAchievements.length === 0 ? (
            <div className="achievements-empty">
              <div className="achievements-empty-emoji">🎮</div>
              <h2 className="achievements-empty-title">No Achievements Yet</h2>
              <p className="achievements-empty-text">Start playing mini-games to unlock achievements!</p>
              <Link to="/scan" className="achievements-cta-button">
                Play Now
              </Link>
            </div>
          ) : (
            Object.keys(groupedAchievements).map((theme) => (
              <div key={theme} className="achievements-theme-section">
                {/* Theme Header */}
                <div className="achievements-theme-header">
                  <h2 className="achievements-theme-title">{theme}</h2>
                </div>

                {/* Coaster Grid */}
                <div className="achievements-grid">
                  {groupedAchievements[theme].map((achievement) => (
                    <div
                      key={achievement.id}
                      className={`achievement-badge ${achievement.earned ? 'unlocked' : 'locked'}`}
                    >
                      <div className="achievement-badge-inner">
                        <span className="achievement-emoji">{achievement.badge_icon}</span>
                        <p className="achievement-name">{achievement.name}</p>
                        
                        {!achievement.earned ? (
                          <div className="achievement-lock-icon">
                            <Lock size={16} color="white" />
                          </div>
                        ) : (
                          <div className="achievement-check-icon">
                            <CheckCircle size={16} color="white" />
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
};

export default Achievements;
